import React, { useCallback, useEffect, useState } from 'react';
import Card from '@src/components/Card';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { REQUEST_STATUS } from '@src/constants/voice';
import { DEFAULT_SERVICE_TYPE, SERVICE_TYPE } from '@src/constants/apiApp';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import Header from './HeaderAppDetail';
import { StyledListAppDetailContainer } from './index.style';
import GeneralInfo from './GeneralInfo';
import Summary from './SummaryDetail';
import RequestsList from './RequestsList';

const AppDetail = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [statistics, setStatistics] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusRequest, setStatusRequest] = useState(REQUEST_STATUS.ALL);
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFetchingApp, setIsFetchingApp] = useState(false);
  const [openUpdateAppStatus, setOpenUpdateAppStatus] = useState(false);
  const [selectedDate, setSelectedDate] = useState({
    value: [
      moment().subtract(7, 'd').startOf('day').toDate(),
      moment().endOf('day').toDate(),
    ],
    label: 'lastWeek',
    type: 'lastWeek',
  });

  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const isNewUIAppDetail = getFeatureValue(FEATURE_KEYS.NEW_UI_APP_DETAIL, {
    userId: user?.id,
    email: user?.email,
  });
  const [activeServiceTab, setActiveServiceTab] =
    useState(DEFAULT_SERVICE_TYPE);

  const handleTabChange = useCallback((tab) => {
    setActiveServiceTab(tab);
    setStatusRequest(REQUEST_STATUS.ALL);
  }, []);

  const handleOpenUpdateAppStatus = () => {
    setOpenUpdateAppStatus(true);
  };

  const handleCloseUpdateAppStatus = () => {
    setOpenUpdateAppStatus(false);
  };

  const handleResetFilter = useCallback(() => {
    setSelectedDate({
      value: [
        moment().subtract(7, 'd').startOf('day').toDate(),
        moment().endOf('day').toDate(),
      ],
      label: 'lastWeek',
      type: 'lastWeek',
    });
    setStatusRequest(REQUEST_STATUS.ALL);
  }, []);

  const appId = location.pathname.split('/')[2];

  const fetchApp = async () => {
    setIsFetchingApp(true);
    const data = await apis.apps.getApp(appId);
    if (data?.status) setApp(data.result.app);
    setIsFetchingApp(false);
  };

  const fetchStatistics = async () => {
    setLoading(true);
    const startDate = selectedDate.value[0];
    const endDate = selectedDate.value[1];

    if (isNewUIAppDetail && activeServiceTab === SERVICE_TYPE.STT) {
      const data = await apis.stt.getSTTStats({ appId, startDate, endDate });
      if (data?.summary) {
        const failureRequests =
          data.byStatus?.find((s) => s.status === 'FAILED')?.count || 0;
        setStatistics({
          totalRequests: data.summary.totalRequests,
          failureRequests,
          totalDuration: data.summary.totalBilledSeconds,
          totalCredits: data.summary.totalBillingCredits,
        });
      }
      setLoading(false);
      return;
    }

    const data = await apis.apps.statsRequestByApp({
      appId,
      startDate,
      endDate,
    });

    setStatistics(data.result);
    setLoading(false);
  };

  const updateAppStatus = async ({ active }) => {
    handleCloseUpdateAppStatus();
    setActionLoading(true);
    const data = await apis.apps.updateApp({ appId, active });
    setActionLoading(false);

    if (!data || !data.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return;
    }

    setApp({ ...app, active });
  };

  useEffect(() => {
    fetchStatistics();
  }, [selectedDate.value, activeServiceTab]);

  useEffect(() => {
    fetchApp();
  }, []);

  return (
    <StyledListAppDetailContainer>
      <Card flexDirection="column" className="list-app-detail-card">
        <div className="list-app-detail-wrapper">
          <Header
            active={app?.active}
            openUpdateAppStatus={openUpdateAppStatus}
            onUpdateAppStatus={updateAppStatus}
            actionLoading={actionLoading}
            onOpenUpdateAppStatus={handleOpenUpdateAppStatus}
            onCloseUpdateAppStatus={handleCloseUpdateAppStatus}
          />
          <GeneralInfo app={app} isLoading={isFetchingApp} />
          <Summary
            statistics={statistics}
            selectedDate={selectedDate}
            handleDateRangeChange={setSelectedDate}
            handleResetFilter={handleResetFilter}
            loading={loading}
            isNewUIAppDetail={isNewUIAppDetail}
            activeServiceTab={activeServiceTab}
            onTabChange={handleTabChange}
          />
          <RequestsList
            startDate={selectedDate.value[0]}
            endDate={selectedDate.value[1]}
            statusRequest={statusRequest}
            setStatusRequest={setStatusRequest}
            isNewUIAppDetail={isNewUIAppDetail}
            activeServiceTab={activeServiceTab}
          />
        </div>
      </Card>
    </StyledListAppDetailContainer>
  );
};

export default AppDetail;
